import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { useIsMobile, DropdownSelect, Grid, Typography, useClientOptions } from '@paintscout/ui'

import { useLocation, useRouteMatch } from 'react-router-dom'
import DocumentTitle from 'src/components/common/DocumentTitle'
import BasePage from 'src/components/layouts/BasePage'
import { BoardView } from './BoardView'
import { ListView } from './ListView'
import type { DropdownSelectOption } from '@paintscout/ui'
import { getPipelines } from '@paintscout/util/builder'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import type { DealUrlQuery } from 'src/components/common/DealSearchBar'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useNavContext } from 'src/components/common/Header/SideNav'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    title: {
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
        width: '100%'
      }
    },
    dropdownSelectIcon: {
      '&$dropdownSelectIcon': {
        width: '24px',
        height: '24px',
        color: theme.paintscout.palette.brand.Neutral.P70
      }
    },
    fillerIcon: {
      display: 'none'
    },
    sortButtonRoot: {
      height: 41,
      width: 41,
      minWidth: 41,
      padding: 0,
      marginRight: theme.spacing(1),
      color: theme.paintscout.palette.brand.Neutral.P75,
      borderColor: theme.paintscout.palette.brand.Neutral.P85,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P60,
        borderColor: theme.paintscout.palette.brand.Neutral.P60
      }
    }
  }),
  { name: 'DealList' }
)

function DealList() {
  const location = useLocation<DealUrlQuery>()
  const urlParams = getUrlQuery<DealUrlQuery>({ location })
  const { setPageSubtitle, setPageChip } = useNavContext()

  const match = useRouteMatch<{
    view?: string
  }>()
  const { view } = match.params
  const isListView = view === 'list'
  const isXs = useIsMobile({ xs: true })
  const { multiPipeline, productionFlag } = useFlags()
  const { options } = useClientOptions()
  const pipelines = getPipelines({ options })

  const pipelineOptions = pipelines.map((pipeline) => ({
    label: pipeline.label,
    value: pipeline.key
  }))
  if (isListView) pipelineOptions.unshift({ label: 'All Pipelines', value: 'all' })

  const viewPipeline = urlParams.pipeline
    ? pipelineOptions.find((pipeline) => pipeline.value === (urlParams?.pipeline ?? 'sales-pipeline'))
    : { label: pipelines[0].label, value: pipelines[0].key }

  const [pipeline, setPipeline] = useState<DropdownSelectOption>(
    !multiPipeline && !productionFlag
      ? pipelineOptions.find((pipeline) => pipeline.value === 'sales-pipeline')
      : viewPipeline
  )

  const handleSelectPipeline = (pipeline: DropdownSelectOption) => {
    setPipeline(pipeline)
    if (isListView) {
      setUrlQuery({
        location,
        params: {
          ...urlParams,
          pipeline: pipeline.value
        }
      })
    } else {
      localStorage.setItem('pipeline', JSON.stringify(pipeline))
    }
  }

  useEffect(() => {
    setPageSubtitle(pipeline.label.replace('Pipeline', '').trim())
    if (pipeline.label === 'Production Pipeline') setPageChip({ variant: 'beta' })
    return () => {
      setPageSubtitle('')
      setPageChip(undefined)
    }
  }, [pipeline.label])

  useEffect(() => {
    if (urlParams.pipeline) {
      const selectedPipeline = pipelineOptions.find((p) => p.value === urlParams.pipeline)
      if (selectedPipeline) {
        setPipeline(selectedPipeline)
      }
    }
  }, [urlParams.pipeline])

  useEffect(() => {
    if (isListView) {
      if (multiPipeline) setPipeline(viewPipeline)
    } else {
      const sortStore = localStorage.getItem('boardViewSort')
      const sort = sortStore ? JSON.parse(sortStore) : 'name'
      setUrlQuery({
        location,
        params: { sort: sort }
      })
      if (multiPipeline) setPipeline(viewPipeline)
    }
  }, [isListView])

  const classes = useStyles()

  return (
    <BasePage className={classes.root} width={'xl'}>
      <DocumentTitle title={'View Deals'} />

      <Grid container justifyContent="space-between" alignItems="center">
        {multiPipeline && (
          <Grid item xs={'auto'} className={classes.title}>
            <DropdownSelect
              classes={{ ownedByViewIcon: classes.dropdownSelectIcon, fillerIcon: classes.fillerIcon }}
              ownedByView
              customOwnedByLabel={<Typography variant={isXs ? 'h3' : 'h1'}>{pipeline.label}</Typography>}
              variant="single"
              value={pipeline}
              options={pipelineOptions}
              onChange={(value) => handleSelectPipeline(value)}
            />
          </Grid>
        )}
      </Grid>

      {isListView ? <ListView pipeline={pipeline.value} /> : <BoardView pipeline={pipeline.value} />}
    </BasePage>
  )
}
export default DealList
